import { useEffect, useState } from 'react'
import { Alert, Button, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material'
import { carregarMeta, salvarMeta } from '../api/conversoes'
import { lerNumero } from '../utils/numero'

const fmt = (v: number | null) => (v == null ? '' : String(v).replace('.', ','))

/**
 * Metas da marca sobre a conversão principal: o diagnóstico do custo, a leitura e o chat
 * passam a dizer "acima da meta", não só "acima da média".
 */
export function MetasMarca({ marcaId, nomeConversao }: { marcaId: string; nomeConversao: string | null }) {
  const [cpa, setCpa] = useState('')
  const [roas, setRoas] = useState('')
  const [salvo, setSalvo] = useState({ cpa: '', roas: '' })
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null)

  useEffect(() => {
    let cancelado = false
    setMsg(null)
    carregarMeta(marcaId)
      .then((m) => {
        if (cancelado) return
        const v = { cpa: fmt(m.custoPorConversao), roas: fmt(m.roas) }
        setCpa(v.cpa)
        setRoas(v.roas)
        setSalvo(v)
      })
      .catch((e: Error) => !cancelado && setMsg({ tipo: 'error', texto: e.message }))
    return () => {
      cancelado = true
    }
  }, [marcaId])

  const nCpa = lerNumero(cpa)
  const nRoas = lerNumero(roas)
  const invalido = (n: number | null) => n != null && (!Number.isFinite(n) || n <= 0)
  const mudou = cpa !== salvo.cpa || roas !== salvo.roas

  const salvar = async () => {
    setSalvando(true)
    setMsg(null)
    try {
      await salvarMeta(marcaId, { custoPorConversao: nCpa, roas: nRoas })
      setSalvo({ cpa, roas })
      setMsg({ tipo: 'success', texto: nCpa == null && nRoas == null ? 'Metas removidas.' : 'Metas salvas.' })
    } catch (e) {
      setMsg({ tipo: 'error', texto: (e as Error).message })
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
        Metas da marca
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Sobre a conversão principal{nomeConversao ? ` (${nomeConversao})` : ''}. Deixe em branco o que não tiver meta.
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
        <TextField
          size="small"
          label="Custo por conversão alvo"
          value={cpa}
          onChange={(e) => setCpa(e.target.value)}
          error={invalido(nCpa)}
          helperText={invalido(nCpa) ? 'Use um valor maior que zero, ex.: 30,00' : ' '}
          slotProps={{ input: { startAdornment: <InputAdornment position="start">R$</InputAdornment> } }}
        />
        <TextField
          size="small"
          label="ROAS alvo"
          value={roas}
          onChange={(e) => setRoas(e.target.value)}
          error={invalido(nRoas)}
          helperText={invalido(nRoas) ? 'Use um valor maior que zero, ex.: 4' : 'Receita ÷ investimento'}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">×</InputAdornment> } }}
        />
        <Button variant="contained" disabled={!mudou || salvando || invalido(nCpa) || invalido(nRoas)} onClick={salvar} sx={{ textTransform: 'none', mt: { sm: 0.25 } }}>
          {salvando ? 'Salvando…' : 'Salvar metas'}
        </Button>
      </Stack>
      {msg ? (
        <Alert severity={msg.tipo} sx={{ mt: 1 }} onClose={() => setMsg(null)}>
          {msg.texto}
        </Alert>
      ) : null}
    </Paper>
  )
}
